/**
 * App — router + provider tree.
 *
 * Layouts:
 *   - AuthLayout: /signup, /login, /forgot-password (no auth required)
 *   - Onboarding: /onboarding (auth required, fullscreen)
 *   - RegularUserShell: every other route (auth required) — renders the
 *     same shell for regular users and admins; admin nav items live in
 *     an "Advanced" dropdown that's hidden for non-admins.
 *
 * Admin-only routes are wrapped in <AdminGuard> in addition to
 * <ProtectedRoute>; non-admin users are redirected to / on access.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { AdminGuard } from '@/components/AdminGuard';
import { FounderGuard } from '@/components/FounderGuard';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RegularUserShell } from '@/layouts/RegularUserShell';

// Simple-mode pages
import { HomePage } from '@/pages/HomePage';
import { SignFilePage } from '@/pages/SignFilePage';
import { ProveIdeaPage } from '@/pages/ProveIdeaPage';
import { CreateDocumentPage } from '@/pages/CreateDocumentPage';
import { OnboardingPage } from '@/pages/OnboardingPage';

// Auth pages
import { SignupPage } from '@/pages/SignupPage';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { AuthVerifyPage } from '@/pages/AuthVerifyPage';
import { AuthResetPasswordPage } from '@/pages/AuthResetPasswordPage';

// Existing feature pages — regular users see Vault / Marketplace /
// Verify / Settings; admins see everything else.
import { VaultPage } from '@/pages/VaultPage';
import { SparkPage } from '@/pages/SparkPage';
import { CoachPage } from '@/pages/CoachPage';
import { VerifyPage } from '@/pages/VerifyPage';
import { GiDetailPage } from '@/pages/GiDetailPage';
import { ExplorePage } from '@/pages/ExplorePage';
// MarketplacePage is preserved on disk for a fast revert; the
// `/marketplace` index now redirects to `/explore` (the new
// regular-user marketplace surface). MarketplaceDetailPage still
// owns `/marketplace/:id` for direct deep-links to a listing.
import { MarketplaceDetailPage } from '@/pages/MarketplaceDetailPage';
import { BlueprintPage } from '@/pages/BlueprintPage';
import { AttestationsPage } from '@/pages/AttestationsPage';
import { AttestationsIndexPage } from '@/pages/AttestationsIndexPage';
import { CeoDashboardPage } from '@/pages/CeoDashboardPage';
import { CommandPage } from '@/pages/CommandPage';
import { ConnectorsPage } from '@/pages/ConnectorsPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { OAuthConnectorCallbackPage } from '@/pages/OAuthConnectorCallbackPage';
import { OmegaPage } from '@/pages/OmegaPage';
import { OmegaBrainPage } from '@/pages/OmegaBrainPage';
import { OmegaOrganPage } from '@/pages/OmegaOrganPage';
import { OmegaSpecialistPage } from '@/pages/OmegaSpecialistPage';
import { OmegaLogPage } from '@/pages/OmegaLogPage';
import { OmegaConstitutionPage } from '@/pages/OmegaConstitutionPage';
import { OmegaEnvelopePage } from '@/pages/OmegaEnvelopePage';
import { OmegaRefuseListPage } from '@/pages/OmegaRefuseListPage';
import { OmegaKillSwitchPage } from '@/pages/OmegaKillSwitchPage';
import { OmegaTriggersPage } from '@/pages/OmegaTriggersPage';
import { OmegaDiscoveryLabPage } from '@/pages/OmegaDiscoveryLabPage';
import { OmegaDecompositionPage } from '@/pages/OmegaDecompositionPage';
import { OmegaMonitorPage } from '@/pages/OmegaMonitorPage';
import { OmegaLayoutShell } from '@/features/omega/cockpit/OmegaSidebar';
import { OmegaActivityPage } from '@/pages/OmegaActivityPage';
import { OmegaCockpitPage } from '@/pages/OmegaCockpitPage';
import { OmegaBudgetPage } from '@/pages/OmegaBudgetPage';
import { OmegaDlmPage } from '@/pages/OmegaDlmPage';
import { OmegaCorpusPage } from '@/pages/OmegaCorpusPage';
import { OmegaMonitorGiPage } from '@/pages/OmegaMonitorGiPage';
import { OmegaBusinessOwnerPage } from '@/pages/OmegaBusinessOwnerPage';
import { OmegaHealthPage } from '@/pages/OmegaHealthPage';
import { OmegaLearningPage } from '@/pages/OmegaLearningPage';
import { OmegaMarketResearchPage } from '@/pages/OmegaMarketResearchPage';
import { OmegaRevenuePage } from '@/pages/OmegaRevenuePage';
import { OmegaChainPage } from '@/pages/OmegaChainPage';
import { OmegaBriefingPage } from '@/pages/OmegaBriefingPage';
import { OmegaDefensePage } from '@/features/omega/defense/DefensePage';
import { OmegaConsolePage } from '@/features/omega/console/ConsolePage';
import { OmegaDecisionsPage } from '@/pages/OmegaDecisionsPage';
import { OmegaProposalsPage } from '@/pages/OmegaProposalsPage';
import { OmegaMemoryPage } from '@/pages/OmegaMemoryPage';
import { OmegaLaunchPlansPage } from '@/features/omega/dashboard/pages/OmegaLaunchPlansPage';
import { OmegaLaunchPlansInsightsPage } from '@/features/omega/dashboard/pages/OmegaLaunchPlansInsightsPage';
import { MarketingDashboardPage } from '@/pages/MarketingDashboardPage';
import { ClientMarketingDashboardPage } from '@/pages/ClientMarketingDashboardPage';
import { ClientMonitorDashboardPage } from '@/pages/ClientMonitorDashboardPage';
import { ClientLoopDashboardPage } from '@/pages/ClientLoopDashboardPage';
import { MarketingCampaignsPage } from '@/pages/MarketingCampaignsPage';
import { CampaignApprovalsPage } from '@/pages/CampaignApprovalsPage';
import { MarketingCampaignDetailPage } from '@/pages/MarketingCampaignDetailPage';
import { MarketingContentPage } from '@/pages/MarketingContentPage';
import { MarketingSchedulePage } from '@/pages/MarketingSchedulePage';
import { MarketingMetricsPage } from '@/pages/MarketingMetricsPage';
import { SalesDashboardPage } from '@/pages/SalesDashboardPage';
import { SalesLeadsPage } from '@/pages/SalesLeadsPage';
import { SalesLeadDetailPage } from '@/pages/SalesLeadDetailPage';
import { SalesApprovalsPage } from '@/pages/SalesApprovalsPage';
import { SalesApprovalDetailPage } from '@/pages/SalesApprovalDetailPage';
import { OutreachApprovalsPage } from '@/pages/OutreachApprovalsPage';
import { SalesApprovalHistoryPage } from '@/pages/SalesApprovalHistoryPage';
import { SalesProposalsPage } from '@/pages/SalesProposalsPage';
import { SalesProposalDetailPage } from '@/pages/SalesProposalDetailPage';
import { SalesPipelineMetricsPage } from '@/pages/SalesPipelineMetricsPage';
import { CustomersDashboardPage } from '@/pages/CustomersDashboardPage';
import { CustomerDetailPage } from '@/pages/CustomerDetailPage';
import { SupportTicketsPage } from '@/pages/SupportTicketsPage';
import { SupportTicketDetailPage } from '@/pages/SupportTicketDetailPage';
import { ChurnAtRiskPage } from '@/pages/ChurnAtRiskPage';
import { CustomerFeedbackPage } from '@/pages/CustomerFeedbackPage';
import { PublicVerifyPage } from '@/pages/PublicVerifyPage';
import { ProofVerifyPage } from '@/pages/ProofVerifyPage';
import { MonitorSignupPage } from '@/pages/monitor/MonitorSignupPage';
import { MonitorConnectPage } from '@/pages/monitor/MonitorConnectPage';
import { MonitorDashboardPage } from '@/pages/monitor/MonitorDashboardPage';
import { AppShell } from '@/pages/app/AppShell';
import { AppStartPage } from '@/pages/app/AppStartPage';
import { PrivacyPage } from '@/pages/app/PrivacyPage';
import { MoneyPage } from '@/pages/app/MoneyPage';
import { RemindersPage } from '@/pages/app/RemindersPage';
import { MePage } from '@/pages/app/MePage';
import { FinanceDashboardPage } from '@/pages/FinanceDashboardPage';
import { FinanceRevenueOverviewPage } from '@/pages/FinanceRevenueOverviewPage';
import { FinanceRevenueEventsPage } from '@/pages/FinanceRevenueEventsPage';
import { FinanceRevenueEventDetailPage } from '@/pages/FinanceRevenueEventDetailPage';
import { FinanceExpensesPage } from '@/pages/FinanceExpensesPage';
import { FinanceExpensesListPage } from '@/pages/FinanceExpensesListPage';
import { FinanceExpensesNewPage } from '@/pages/FinanceExpensesNewPage';
import { FinanceExpenseDetailPage } from '@/pages/FinanceExpenseDetailPage';
import { FinanceReportsPage } from '@/pages/FinanceReportsPage';
import { FinanceReportDetailPage } from '@/pages/FinanceReportDetailPage';
import { FinanceTaxOverviewPage } from '@/pages/FinanceTaxOverviewPage';
import { FinanceTaxVatPage } from '@/pages/FinanceTaxVatPage';
import { FinanceTaxWithholdingPage } from '@/pages/FinanceTaxWithholdingPage';
import { FinanceTaxForm23Page } from '@/pages/FinanceTaxForm23Page';
import { FinancePLPage } from '@/pages/FinancePLPage';
import { FinanceBalanceSheetPage } from '@/pages/FinanceBalanceSheetPage';
import { FinanceHashavshevetPage } from '@/pages/FinanceHashavshevetPage';
import { FinanceMonthClosePage } from '@/pages/FinanceMonthClosePage';
import { FinancePeriodClosesPage } from '@/pages/FinancePeriodClosesPage';
import { FinanceApprovalQueuePage } from '@/pages/FinanceApprovalQueuePage';
import { FinanceApprovalDetailPage } from '@/pages/FinanceApprovalDetailPage';
import { AlphaOmegaDashboardPage } from '@/pages/AlphaOmegaDashboardPage';
import { AlphaOmegaCompanyPage } from '@/pages/AlphaOmegaCompanyPage';
import { AlphaOmegaSectorSignalsPage } from '@/pages/AlphaOmegaSectorSignalsPage';
import { AlphaOmegaSectorSignalDetailPage } from '@/pages/AlphaOmegaSectorSignalDetailPage';
import { AlphaOmegaSpawnProposalsPage } from '@/pages/AlphaOmegaSpawnProposalsPage';
import { AlphaOmegaSpawnProposalDetailPage } from '@/pages/AlphaOmegaSpawnProposalDetailPage';
import { AlphaOmegaMonthlyPlPage } from '@/pages/AlphaOmegaMonthlyPlPage';
import { AlphaOmegaMonthlyPlDetailPage } from '@/pages/AlphaOmegaMonthlyPlDetailPage';
import { AlphaOmegaRoyaltyTrackingPage } from '@/pages/AlphaOmegaRoyaltyTrackingPage';
import { AlphaOmegaWorkerAllocationPage } from '@/pages/AlphaOmegaWorkerAllocationPage';
import { AlphaOmegaApproveQueuePage } from '@/pages/AlphaOmegaApproveQueuePage';
import { CeoAlphaOmegaAdvisorPage } from '@/pages/CeoAlphaOmegaAdvisorPage';
import { CeoCrossBrainPage } from '@/pages/CeoCrossBrainPage';
import { CeoCalendarPage } from '@/pages/CeoCalendarPage';
import { OmegaCeoDashboardPage } from '@/pages/OmegaCeoDashboardPage';
import { OmegaCeoBriefingsPage } from '@/pages/OmegaCeoBriefingsPage';
import { OmegaCeoBriefingDetailPage } from '@/pages/OmegaCeoBriefingDetailPage';
import { OmegaCeoDecisionsPage } from '@/pages/OmegaCeoDecisionsPage';
import { MarketIntelDashboardPage } from '@/pages/MarketIntelDashboardPage';
import { MarketIntelTamPage } from '@/pages/MarketIntelTamPage';
import { MarketIntelCompetitorsPage } from '@/pages/MarketIntelCompetitorsPage';
import { MarketIntelRegulatoryPage } from '@/pages/MarketIntelRegulatoryPage';
import { MarketIntelPricingPage } from '@/pages/MarketIntelPricingPage';
import { MarketIntelGateStatusPage } from '@/pages/MarketIntelGateStatusPage';
import { LearningGatesPage } from '@/pages/LearningGatesPage';
import { BackfillStatusPage } from '@/pages/BackfillStatusPage';
import { BackfillParallelPage } from '@/pages/BackfillParallelPage';
import { ResearchTeamsPage } from '@/pages/ResearchTeamsPage';
import { OmegaProductsPage } from '@/pages/OmegaProductsPage';
import { OmegaDiscoveryPage } from '@/pages/OmegaDiscoveryPage';
import { PipelineGatePage } from '@/pages/PipelineGatePage';
import { OmegaPipelinePage } from '@/pages/OmegaPipelinePage';
import { OmegaCommanderPage } from '@/pages/OmegaCommanderPage';
import { OmegaLoopPage } from '@/pages/OmegaLoopPage';
import { OmegaFounderSpecsPage } from '@/pages/OmegaFounderSpecsPage';

import { DemoPage } from '@/pages/DemoPage';
import { MonitorDemoPage } from '@/pages/MonitorDemoPage';
import { MonitorDemoPage as MonitorConsumerDemoPage } from '@/pages/monitor/MonitorDemoPage';

import { CreatorStorePage } from '@/pages/CreatorStorePage';
import { ProductPage } from '@/features/marketplace/ProductPage';
import { CreatorPage } from '@/features/creators/CreatorPage';
import { EarningsDashboardPage } from '@/pages/EarningsDashboardPage';
import { SimpleWizardPage } from '@/pages/SimpleWizardPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const adminRoute = (path: string, element: React.ReactNode) => ({
  path,
  element: <AdminGuard>{element}</AdminGuard>,
});

const founderRoute = (path: string, element: React.ReactNode) => ({
  path,
  element: <FounderGuard>{element}</FounderGuard>,
});

const router = createBrowserRouter([
  // ── Auth (unauthenticated) ───────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: '/signup',               element: <SignupPage /> },
      { path: '/login',                element: <LoginPage /> },
      { path: '/forgot-password',      element: <ForgotPasswordPage /> },
      // OAuth + email-verification + password-reset landing pages.
      // All three render the AuthLayout (no auth guard) because they
      // own the act of establishing the session.
      { path: '/auth/callback',        element: <AuthCallbackPage /> },
      { path: '/auth/verify',          element: <AuthVerifyPage /> },
      { path: '/auth/reset-password',  element: <AuthResetPasswordPage /> },
    ],
  },

  // ── Onboarding (authenticated, full-screen) ──────────────────────
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },

  // OAuth-redirect landing for the external connectors (Google × 3).
  // Standalone protected route — no shell chrome — because Google
  // redirects directly here with `?code=&state=` and we want a clean
  // "finishing up" pane while the API exchange runs.
  {
    path: '/oauth/connector-callback/:slug',
    element: (
      <ProtectedRoute>
        <OAuthConnectorCallbackPage />
      </ProtectedRoute>
    ),
  },

  // Public verifier — `/v/:id`. NO auth, NO shell. Lands directly so
  // a phone-camera QR scan resolves to a clean "Verified by GI Engine"
  // page. Backed by the `UNAUTH_GET_PATHS` allow-list on the API
  // server (auth.rs) — public GET on `/api/v1/gi/{id}` and `/verify`.
  {
    path: '/v/:id',
    element: <PublicVerifyPage />,
  },

  // Phase 13 — provenance proof verifier. Public, no auth, no shell.
  // Anyone can paste a proof JSON blob from GET /api/v1/proof/{seq}
  // and verify the engine's ed25519 signature server-side.
  {
    path: '/proof/verify',
    element: <ProofVerifyPage />,
  },

  // Monitor — the client product flow. PUBLIC (no auth, no shell): a real
  // external client signs up, connects/sends their data, and reads their own
  // signed insights. Token-gated client-side via localStorage `gi_monitor_token`
  // (connect + dashboard redirect to /monitor/start when it's missing). The
  // signup call itself is unauthenticated.
  {
    path: '/monitor/start',
    element: <MonitorSignupPage />,
  },
  {
    path: '/monitor/connect',
    element: <MonitorConnectPage />,
  },
  {
    path: '/monitor/dashboard',
    element: <MonitorDashboardPage />,
  },

  // The FREE personal Monitor app — installable, mobile-first PWA. PUBLIC
  // (no auth, no operator shell). Onboarding (`/app`, `/app/start`) renders
  // standalone; the four inner screens render inside AppShell (bottom tab bar
  // on phone, side rail on desktop) and are token-gated client-side via
  // localStorage `gi_monitor_token` (AppShell bounces to /app/start when it's
  // missing). The signup call itself is unauthenticated. start_url is `/app`.
  { path: '/app/start', element: <AppStartPage /> },
  {
    path: '/app',
    children: [
      { index: true, element: <AppStartPage /> },
      {
        element: <AppShell />,
        children: [
          { path: 'privacy', element: <PrivacyPage /> },
          { path: 'money', element: <MoneyPage /> },
          { path: 'reminders', element: <RemindersPage /> },
          { path: 'me', element: <MePage /> },
        ],
      },
    ],
  },

  // Public signing demo — `/demo`. NO auth, NO shell. Five-persona
  // playground that generates a real Ed25519 proof in the browser
  // (hardcoded DEMO key, no server calls) and shows a QR linking to
  // the verifier route above. Used in pitches and on the homepage CTA.
  // Public Monitor demo — `/demo`. NO auth, NO shell. No-signup, fully-simulated
  // Monitor walkthrough (animated scan, savings counter, signed business insights)
  // funnelling to "Sign up to see YOUR real data". The client-facing WOW front door.
  {
    path: '/demo',
    element: <MonitorDemoPage />,
  },
  // Public signing playground — `/playground` (was `/demo`). Five-persona Ed25519
  // proof generator in the browser. Technical / dev-facing.
  {
    path: '/playground',
    element: <DemoPage />,
  },
  // /poc removed 2026-06-18 — the live demo (Llama on GPU) is offline until
  // June 23; the static fallback was misleading. No demo at /poc until then.
  // Public consumer WOW demo — `/monitor/demo`. NO auth, bilingual (He/En),
  // mobile-first, animated scan + savings; CTA to the free app. (W2 Monitor UX.)
  {
    path: '/monitor/demo',
    element: <MonitorConsumerDemoPage />,
  },

  // Public creator storefront — `/store/:creator`. NO auth, NO shell.
  // Reserved for a future creator program; no creator profiles are
  // registered today, so every slug bounces back to /marketplace.
  {
    path: '/store/:creator',
    element: <CreatorStorePage />,
  },

  // Simple-mode onboarding wizard — `/simple`. NO auth, NO shell. The
  // "front door" for visitors who don't yet know what cryptographic
  // signing means: pick a job, pick a pain, get a recommendation.
  {
    path: '/simple',
    element: <SimpleWizardPage />,
  },

  // ── Single shell for every signed-in user ────────────────────────
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RegularUserShell />
      </ProtectedRoute>
    ),
    children: [
      // Regular routes — visible to every signed-in user.
      { index: true,                      element: <HomePage /> },
      { path: 'sign-file',                element: <SignFilePage /> },
      { path: 'prove-idea',               element: <ProveIdeaPage /> },
      { path: 'create-document',          element: <CreateDocumentPage /> },
      { path: 'vault',                    element: <VaultPage /> },
      { path: 'gi/:id',                   element: <GiDetailPage /> },
      { path: 'gi/:id/attestations',      element: <AttestationsPage /> },
      { path: 'explore',                  element: <ExplorePage /> },
      // `/marketplace` redirects to `/explore` so existing nav links
      // and bookmarks don't 404 while the new surface takes over.
      { path: 'marketplace',              element: <Navigate to="/explore" replace /> },
      { path: 'marketplace/:id',          element: <MarketplaceDetailPage /> },
      { path: 'products/:slug',           element: <ProductPage /> },
      { path: 'creators/:pubkey',         element: <CreatorPage /> },
      { path: 'verify',                   element: <VerifyPage /> },
      { path: 'settings',                 element: <SettingsPage /> },
      // Connectors is a regular-user feature — every user needs to wire
      // their tools (Stripe, QuickBooks, Google Drive, HL7 FHIR, etc.)
      // before their starter GIs can do anything useful.
      { path: 'connectors',               element: <ConnectorsPage /> },
      // External connectors — five fixed providers that pull signed
      // proofs from the user's Google / Stripe / local files. Distinct
      // from the user-defined Connectors above (which point at custom
      // endpoints).
      { path: 'integrations',             element: <IntegrationsPage /> },

      // Spark is reachable by every signed-in user so the marketing
      // site's "Start Using" CTA (`/spark?product=<slug>`) lands on a
      // working wizard instead of bouncing to /. It still stays out
      // of the regular nav (only the Advanced dropdown surfaces it),
      // so the discoverability posture is unchanged.
      { path: 'spark',                    element: <SparkPage /> },

      // Creator earnings dashboard — auth required. The brief calls
      // for a creator-role gate; for now every signed-in user can
      // reach this URL. CreatorGuard wraps it once that role lands.
      { path: 'dashboard/earnings',       element: <EarningsDashboardPage /> },

      // Admin-only routes — non-admin users redirect to / silently.
      adminRoute('conductor',     <BlueprintPage />),
      adminRoute('blueprint',     <BlueprintPage />),
      adminRoute('attestations',  <AttestationsIndexPage />),
      adminRoute('coach',         <CoachPage />),
      adminRoute('command',       <CommandPage />),
      // ── Non-omega founder routes ────────────────────────────────────
      // S59 Phase 2 — outreach Founder-approval inbox (legacy non-omega path kept for compat).
      founderRoute('outreach',         <OutreachApprovalsPage />),
      founderRoute('product/monitor',  <ClientMonitorDashboardPage />),
      founderRoute('product/loop',     <ClientLoopDashboardPage />),
      adminRoute('ceo',                <CeoDashboardPage />),

      // ── OMEGA layout group — OmegaSidebar persists across all /omega/* ──
      // Pathless layout route: FounderGuard + OmegaLayoutShell wrap every
      // child. Children keep their full path; no nesting-depth change.
      {
        element: <FounderGuard><OmegaLayoutShell /></FounderGuard>,
        children: [
          { path: 'omega',                       element: <OmegaCockpitPage /> },
          { path: 'omega/home',                  element: <OmegaPage /> },
          { path: 'omega/cockpit',               element: <OmegaCockpitPage /> },
          { path: 'omega/constitution',          element: <OmegaConstitutionPage /> },
          { path: 'omega/envelope',              element: <OmegaEnvelopePage /> },
          { path: 'omega/refuse-list',           element: <OmegaRefuseListPage /> },
          { path: 'omega/kill-switch',           element: <OmegaKillSwitchPage /> },
          { path: 'omega/log',                   element: <OmegaLogPage /> },
          { path: 'omega/triggers',              element: <OmegaTriggersPage /> },
          { path: 'omega/activity',              element: <OmegaActivityPage /> },
          { path: 'omega/budget',                element: <OmegaBudgetPage /> },
          { path: 'omega/revenue',               element: <OmegaRevenuePage /> },
          { path: 'omega/products',              element: <OmegaProductsPage /> },
          { path: 'omega/decomposition',         element: <OmegaDecompositionPage /> },
          { path: 'omega/dlm',                   element: <OmegaDlmPage /> },
          { path: 'omega/corpus-proof',          element: <OmegaCorpusPage /> },
          { path: 'omega/monitor-gi',            element: <OmegaMonitorGiPage /> },
          { path: 'omega/business',              element: <OmegaBusinessOwnerPage /> },
          { path: 'omega/health',                element: <OmegaHealthPage /> },
          { path: 'omega/learning',              element: <OmegaLearningPage /> },
          { path: 'omega/market-research',       element: <OmegaMarketResearchPage /> },
          { path: 'omega/chain',                 element: <OmegaChainPage /> },
          { path: 'omega/briefing',              element: <OmegaBriefingPage /> },
          { path: 'omega/defense',              element: <OmegaDefensePage /> },
          { path: 'omega/console',              element: <OmegaConsolePage /> },
          { path: 'omega/decisions',            element: <OmegaDecisionsPage /> },
          { path: 'omega/proposals',            element: <OmegaProposalsPage /> },
          { path: 'omega/memory',               element: <OmegaMemoryPage /> },
          { path: 'omega/launchplans',          element: <OmegaLaunchPlansPage /> },
          { path: 'omega/launchplans/insights', element: <OmegaLaunchPlansInsightsPage /> },
          { path: 'omega/marketing',                          element: <MarketingDashboardPage /> },
          { path: 'omega/marketing/campaigns',                element: <MarketingCampaignsPage /> },
          { path: 'omega/marketing/approvals',                element: <CampaignApprovalsPage /> },
          { path: 'omega/marketing/campaigns/:id',            element: <MarketingCampaignDetailPage /> },
          { path: 'omega/marketing/content/:id',              element: <MarketingContentPage /> },
          { path: 'omega/marketing/schedule',                 element: <MarketingSchedulePage /> },
          { path: 'omega/marketing/metrics',                  element: <MarketingMetricsPage /> },
          { path: 'omega/marketing/clients',                  element: <ClientMarketingDashboardPage /> },
          { path: 'omega/sales',                              element: <SalesDashboardPage /> },
          { path: 'omega/sales/leads',                        element: <SalesLeadsPage /> },
          { path: 'omega/sales/leads/:id',                    element: <SalesLeadDetailPage /> },
          { path: 'omega/sales/approvals',                    element: <SalesApprovalsPage /> },
          { path: 'omega/outreach',                           element: <OutreachApprovalsPage /> },
          { path: 'omega/sales/approvals/history',            element: <SalesApprovalHistoryPage /> },
          { path: 'omega/sales/approvals/:item_id',           element: <SalesApprovalDetailPage /> },
          { path: 'omega/sales/proposals',                    element: <SalesProposalsPage /> },
          { path: 'omega/sales/proposals/:id',                element: <SalesProposalDetailPage /> },
          { path: 'omega/sales/pipeline',                     element: <SalesPipelineMetricsPage /> },
          { path: 'omega/customers',                          element: <CustomersDashboardPage /> },
          { path: 'omega/customers/list',                     element: <CustomersDashboardPage /> },
          { path: 'omega/customers/support',                  element: <SupportTicketsPage /> },
          { path: 'omega/customers/support/:customerId/:ticketId', element: <SupportTicketDetailPage /> },
          { path: 'omega/customers/churn',                    element: <ChurnAtRiskPage /> },
          { path: 'omega/customers/feedback',                 element: <CustomerFeedbackPage /> },
          { path: 'omega/customers/:id',                      element: <CustomerDetailPage /> },
          { path: 'omega/finance',                               element: <FinanceDashboardPage /> },
          { path: 'omega/finance/revenue',                       element: <FinanceRevenueOverviewPage /> },
          { path: 'omega/finance/revenue/events',                element: <FinanceRevenueEventsPage /> },
          { path: 'omega/finance/revenue/events/:event_id',      element: <FinanceRevenueEventDetailPage /> },
          { path: 'omega/finance/expenses',                      element: <FinanceExpensesPage /> },
          { path: 'omega/finance/expenses/list',                 element: <FinanceExpensesListPage /> },
          { path: 'omega/finance/expenses/new',                  element: <FinanceExpensesNewPage /> },
          { path: 'omega/finance/expenses/:expense_id',          element: <FinanceExpenseDetailPage /> },
          { path: 'omega/finance/reports',                       element: <FinanceReportsPage /> },
          { path: 'omega/finance/reports/:report_id',            element: <FinanceReportDetailPage /> },
          { path: 'omega/finance/tax',                           element: <FinanceTaxOverviewPage /> },
          { path: 'omega/finance/tax/vat',                       element: <FinanceTaxVatPage /> },
          { path: 'omega/finance/tax/withholding',               element: <FinanceTaxWithholdingPage /> },
          { path: 'omega/finance/tax/form-23',                   element: <FinanceTaxForm23Page /> },
          { path: 'omega/finance/pl',                            element: <FinancePLPage /> },
          { path: 'omega/finance/balance-sheet',                 element: <FinanceBalanceSheetPage /> },
          { path: 'omega/finance/hashavshevet',                  element: <FinanceHashavshevetPage /> },
          { path: 'omega/finance/month-close',                   element: <FinanceMonthClosePage /> },
          { path: 'omega/finance/period-closes',                 element: <FinancePeriodClosesPage /> },
          { path: 'omega/finance/approve',                       element: <FinanceApprovalQueuePage /> },
          { path: 'omega/finance/approve/:item_type/:item_id',   element: <FinanceApprovalDetailPage /> },
          { path: 'omega/alpha-omega',                                        element: <AlphaOmegaDashboardPage /> },
          { path: 'omega/alpha-omega/approve',                                element: <AlphaOmegaApproveQueuePage /> },
          { path: 'omega/alpha-omega/portfolio/:company_id',                  element: <AlphaOmegaCompanyPage /> },
          { path: 'omega/alpha-omega/sector-signals',                         element: <AlphaOmegaSectorSignalsPage /> },
          { path: 'omega/alpha-omega/sector-signals/:signal_id',              element: <AlphaOmegaSectorSignalDetailPage /> },
          { path: 'omega/alpha-omega/spawn-proposals',                        element: <AlphaOmegaSpawnProposalsPage /> },
          { path: 'omega/alpha-omega/spawn-proposals/:proposal_id',           element: <AlphaOmegaSpawnProposalDetailPage /> },
          { path: 'omega/alpha-omega/monthly-pl',                             element: <AlphaOmegaMonthlyPlPage /> },
          { path: 'omega/alpha-omega/monthly-pl/:pl_id',                      element: <AlphaOmegaMonthlyPlDetailPage /> },
          { path: 'omega/alpha-omega/royalty-tracking',                       element: <AlphaOmegaRoyaltyTrackingPage /> },
          { path: 'omega/alpha-omega/worker-allocation',                      element: <AlphaOmegaWorkerAllocationPage /> },
          // brain/* — static 'discovery' must be listed before the dynamic ':brainId' catch
          { path: 'omega/brain/discovery',                       element: <OmegaDiscoveryPage /> },
          { path: 'omega/brain/discovery/:labName/outputs',      element: <OmegaDiscoveryLabPage /> },
          { path: 'omega/brain/:brainId',                        element: <OmegaBrainPage /> },
          { path: 'omega/brain/:brainId/:organId',               element: <OmegaOrganPage /> },
          { path: 'omega/specialist/:specialistId',              element: <OmegaSpecialistPage /> },
          { path: 'omega/ceo',                                   element: <OmegaCeoDashboardPage /> },
          { path: 'omega/ceo/briefings',                         element: <OmegaCeoBriefingsPage /> },
          { path: 'omega/ceo/briefings/:briefing_id',            element: <OmegaCeoBriefingDetailPage /> },
          { path: 'omega/ceo/decisions',                         element: <OmegaCeoDecisionsPage /> },
          { path: 'omega/ceo/alpha-omega-advisor',               element: <CeoAlphaOmegaAdvisorPage /> },
          { path: 'omega/ceo/cross-brain',                       element: <CeoCrossBrainPage /> },
          { path: 'omega/ceo/calendar',                          element: <CeoCalendarPage /> },
          // Market Intelligence — static sub-paths before catch-all siblings
          { path: 'omega/market-intel',                          element: <MarketIntelDashboardPage /> },
          { path: 'omega/market-intel/tam',                      element: <MarketIntelTamPage /> },
          { path: 'omega/market-intel/competitors',              element: <MarketIntelCompetitorsPage /> },
          { path: 'omega/market-intel/regulatory',               element: <MarketIntelRegulatoryPage /> },
          { path: 'omega/market-intel/pricing',                  element: <MarketIntelPricingPage /> },
          { path: 'omega/market-intel/gate-status',              element: <MarketIntelGateStatusPage /> },
          { path: 'omega/learning/gates',                        element: <LearningGatesPage /> },
          { path: 'omega/system/backfill',                       element: <BackfillStatusPage /> },
          { path: 'omega/backfill/parallel',                     element: <BackfillParallelPage /> },
          { path: 'omega/research/teams',                        element: <ResearchTeamsPage /> },
          { path: 'omega/monitor',                               element: <OmegaMonitorPage /> },
          { path: 'omega/pipeline/gate',                         element: <PipelineGatePage /> },
          { path: 'omega/pipeline',                              element: <OmegaPipelinePage /> },
          { path: 'omega/commander',                             element: <OmegaCommanderPage /> },
          { path: 'omega/loop',                                  element: <OmegaLoopPage /> },
          { path: 'omega/founder-specs',                         element: <OmegaFounderSpecsPage /> },
        ],
      },

      // Legacy "advanced mode" entry point — redirect to Vault for
      // continuity with the previous footer link.
      { path: 'advanced', element: <Navigate to="/vault" replace /> },
    ],
  },

  // ── 404 ──────────────────────────────────────────────────────────
  { path: '*', element: <NotFoundPage /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
